import { useCallback, useEffect, useRef, useState } from 'react';

/**
 * Rolling sample used to derive an exponentially-smoothed upload rate.
 * `smoothedRate` is expressed in percentage points per millisecond so it can
 * be divided into the outstanding percentage to yield remaining milliseconds
 * directly.
 */
interface Sample {
    lastProgress: number;
    lastAt: number;
    smoothedRate: number | undefined;
}

/**
 * Weight applied to the most-recent instantaneous rate. Empirically chosen
 * to react to real speed changes within a few samples while still absorbing
 * jitter from noisy progress callbacks.
 */
const RATE_ALPHA = 0.3;

/**
 * Refresh cadence for the *displayed* estimate, matching the Figma spec
 * (Anna's 2026-07-15 UX review): under 30 s → every 5 s, 30–60 s → every
 * 10 s, 1–5 min → every 30 s, 5–10 min → every 60 s, ≥ 10 min → every 60 s
 * (the design allows 60–120 s in that bucket; we stay at the lower bound
 * for a slightly more responsive tail-end). `formatRemainingTime` already
 * collapses values into the same buckets, so recomputing at a higher rate
 * would only churn without ever changing the label.
 */
function pickIntervalMs(remainingMs: number): number {
    if (remainingMs < 30_000) {
        return 5_000;
    }
    if (remainingMs < 60_000) {
        return 10_000;
    }
    if (remainingMs < 5 * 60_000) {
        return 30_000;
    }
    if (remainingMs < 10 * 60_000) {
        return 60_000;
    }
    return 60_000;
}

/**
 * Estimates remaining upload time in milliseconds from live progress
 * observations. Consumers pass the current `progress` (0–100), the
 * `startedAt` timestamp of the upload, and an `isActive` flag; the hook
 * measures the effective upload rate, applies exponential smoothing, and
 * refreshes the returned value at intervals matching the display-bucket
 * cadence so the countdown feels calm.
 *
 * Returns `undefined` until enough data has been observed to produce a
 * meaningful rate, or when the upload is not currently active.
 */
export function useEstimatedRemainingTime(
    progress: number,
    startedAt: number | undefined,
    isActive: boolean,
): number | undefined {
    const sampleRef = useRef<Sample>({
        lastProgress: 0,
        lastAt: 0,
        smoothedRate: undefined,
    });
    const [estimateMs, setEstimateMs] = useState<number | undefined>(undefined);

    useEffect(() => {
        if (!isActive || typeof startedAt !== 'number' || !Number.isFinite(progress)) {
            return;
        }
        const now = Date.now();
        const sample = sampleRef.current;

        if (sample.lastAt === 0) {
            const elapsed = Math.max(1, now - startedAt);
            const bootstrapRate = progress > 0 ? progress / elapsed : undefined;
            sampleRef.current = {
                lastProgress: progress,
                lastAt: now,
                smoothedRate: bootstrapRate,
            };
            return;
        }

        if (progress > sample.lastProgress) {
            const dt = now - sample.lastAt;
            if (dt > 0) {
                const instantRate = (progress - sample.lastProgress) / dt;
                const prev = sample.smoothedRate;
                sampleRef.current = {
                    lastProgress: progress,
                    lastAt: now,
                    smoothedRate:
                        typeof prev === 'number'
                            ? RATE_ALPHA * instantRate + (1 - RATE_ALPHA) * prev
                            : instantRate,
                };
            }
        }
    }, [progress, startedAt, isActive]);

    const recompute = useCallback((): number | undefined => {
        const sample = sampleRef.current;
        if (typeof sample.smoothedRate !== 'number' || sample.smoothedRate <= 0) {
            return undefined;
        }
        const remainingProgress = Math.max(0, 100 - sample.lastProgress);
        if (remainingProgress === 0) {
            return 0;
        }
        return remainingProgress / sample.smoothedRate;
    }, []);

    useEffect(() => {
        if (!isActive) {
            setEstimateMs(undefined);
            sampleRef.current = {
                lastProgress: 0,
                lastAt: 0,
                smoothedRate: undefined,
            };
            return;
        }

        let cancelled = false;
        let timer: ReturnType<typeof setTimeout> | undefined;

        const tick = () => {
            if (cancelled) {
                return;
            }
            const next = recompute();
            setEstimateMs(next);
            const delay = typeof next === 'number' ? pickIntervalMs(next) : 1_000;
            timer = setTimeout(tick, delay);
        };

        // Small warm-up delay so the first progress observation has a chance
        // to land before the initial recompute.
        timer = setTimeout(tick, 250);

        return () => {
            cancelled = true;
            if (timer) {
                clearTimeout(timer);
            }
        };
    }, [isActive, recompute]);

    return estimateMs;
}
